import Stores from '../config/stores';

const PROFILE_STORAGE_PREFIX = 'budgetvenlig.profile.v1';
export const PROFILE_UPDATED_EVENT = 'budgetvenlig:profile-updated';

const toFiniteNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

export const getCookieValue = (name) => {
    if (typeof document === 'undefined') {
        return '';
    }

    const rawValue = document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${name}=`))
        ?.slice(name.length + 1);

    if (!rawValue) {
        return '';
    }

    try {
        return decodeURIComponent(rawValue);
    } catch {
        return rawValue;
    }
};

export const getCurrentUserEmail = () => getCookieValue('email').trim().toLowerCase();

export const clearCurrentSession = () => {
    if (typeof document === 'undefined') {
        return;
    }

    const cookieOptions = window.location.protocol === 'https:'
        ? 'Path=/; SameSite=None; Secure'
        : 'Path=/; SameSite=Lax';

    document.cookie = `email=; Max-Age=0; ${cookieOptions}`;
    document.cookie = `session_token=; Max-Age=0; ${cookieOptions}`;
    window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
};

export const createEmptyProfile = (email = '') => ({
    email,
    address: '',
    radius: 2,
    geoLocation: {},
    preferredStoreKeys: [],
    weeklyBudget: null,
    allowAllNearbyStores: false,
    inventoryMode: 'precise',
    favoriteRecipes: [],
    favoriteDealAlertsEnabled: false,
    favoriteDealThreshold: 50,
    browserDealNotificationsEnabled: false,
});

export const normalizeProfile = (profile, email = '') => {
    const fallback = createEmptyProfile(email);
    const radius = toFiniteNumber(profile?.radius);
    const weeklyBudget = toFiniteNumber(profile?.weeklyBudget);
    const lat = toFiniteNumber(profile?.geoLocation?.lat);
    const lon = toFiniteNumber(profile?.geoLocation?.lon);
    const dealThreshold = toFiniteNumber(profile?.favoriteDealThreshold);
    const favoriteRecipes = (Array.isArray(profile?.favoriteRecipes) ? profile.favoriteRecipes : [])
        .map((favorite) => typeof favorite === 'string'
            ? { recipeId: favorite, title: favorite }
            : {
                recipeId: String(favorite?.recipeId || '').trim(),
                title: String(favorite?.title || favorite?.recipeId || '').trim(),
                image: String(favorite?.image || '').trim(),
            })
        .filter((favorite) => favorite.recipeId)
        .filter((favorite, index, array) => (
            array.findIndex((candidate) => candidate.recipeId === favorite.recipeId) === index
        ));

    return {
        ...fallback,
        email: String(email || profile?.email || '').trim().toLowerCase(),
        address: String(profile?.address || '').trim(),
        radius: radius === null ? fallback.radius : Math.max(1, Math.min(20, radius)),
        geoLocation: lat === null || lon === null
            ? {}
            : { lat: String(lat), lon: String(lon) },
        preferredStoreKeys: Array.from(new Set(
            (Array.isArray(profile?.preferredStoreKeys) ? profile.preferredStoreKeys : [])
                .filter((storeKey) => Stores[storeKey])
        )),
        weeklyBudget: weeklyBudget === null || weeklyBudget <= 0
            ? null
            : Math.round(weeklyBudget),
        allowAllNearbyStores: Boolean(profile?.allowAllNearbyStores),
        inventoryMode: profile?.inventoryMode === 'smart' ? 'smart' : 'precise',
        favoriteRecipes,
        favoriteDealAlertsEnabled: Boolean(profile?.favoriteDealAlertsEnabled),
        favoriteDealThreshold: dealThreshold === null
            ? fallback.favoriteDealThreshold
            : Math.max(5, Math.min(90, Math.round(dealThreshold))),
        browserDealNotificationsEnabled: Boolean(profile?.browserDealNotificationsEnabled),
    };
};

const getProfileStorageKey = (email) => (
    email ? `${PROFILE_STORAGE_PREFIX}:${email}` : `${PROFILE_STORAGE_PREFIX}:guest`
);

export const loadProfile = (email = getCurrentUserEmail()) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();

    if (typeof window === 'undefined') {
        return createEmptyProfile(normalizedEmail);
    }

    try {
        const savedValue = window.localStorage.getItem(getProfileStorageKey(normalizedEmail));
        return normalizeProfile(savedValue ? JSON.parse(savedValue) : null, normalizedEmail);
    } catch {
        return createEmptyProfile(normalizedEmail);
    }
};

export const saveProfile = (profile, email = getCurrentUserEmail()) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const normalizedProfile = normalizeProfile(profile, normalizedEmail);

    if (typeof window !== 'undefined') {
        window.localStorage.setItem(
            getProfileStorageKey(normalizedEmail),
            JSON.stringify(normalizedProfile)
        );
        window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT, {
            detail: { email: normalizedEmail, profile: normalizedProfile },
        }));
    }

    return normalizedProfile;
};

export const applyPreferredStores = (stores, preferredStoreKeys) => {
    const preferredStoreKeySet = new Set(
        (Array.isArray(preferredStoreKeys) ? preferredStoreKeys : [])
            .filter((storeKey) => Stores[storeKey])
    );

    return Object.keys(stores).reduce((nextStores, storeKey) => {
        nextStores[storeKey] = {
            ...stores[storeKey],
            selected: preferredStoreKeySet.has(storeKey),
        };
        return nextStores;
    }, {});
};
